/**
 * What a purchase order says to a supplier. Spec 012, FR-1221 and FR-1222.
 *
 * One shape for the email and the PDF, so the two cannot say different
 * things. The server builds it from the order; the email template and the PDF
 * writer only lay it out.
 */
import { gstOnTop } from './money';

export interface PurchaseOrderDocLine {
  supplier_code: string | null;
  code: string;
  product: string;
  colour: string | null;
  size: string | null;
  qty_ordered: number;
  uom: string;
  /** Without GST. 0 with `free_of_charge`. */
  unit_cost_cents: number;
  free_of_charge: boolean;
  line_total_cents: number;
}

export interface PurchaseOrderDoc {
  ref: string;
  /** YYYY-MM-DD. */
  order_date: string;
  expected_at: string | null;
  company_name: string;
  company_abn: string | null;
  supplier_name: string;
  /** The delivery location's site, in one line. Null is "We collect". */
  deliver_to: string | null;
  note: string | null;
  lines: PurchaseOrderDocLine[];
  total_ex_gst_cents: number;
  gst_cents: number;
  total_inc_gst_cents: number;
}

/**
 * The totals of an order: without GST, the GST, and with GST (FR-1204). A
 * line with a blank price counts nothing and is reported, because a total
 * that leaves a line out without saying so is a wrong total.
 */
export function purchaseOrderTotals(
  lines: Array<{ qty_ordered: number; unit_cost_cents: number | null }>,
): { ex_gst_cents: number; gst_cents: number; inc_gst_cents: number; blank_lines: number } {
  let ex = 0;
  let blank = 0;
  for (const l of lines) {
    if (l.unit_cost_cents == null) { blank += 1; continue; }
    ex += Math.round(Number(l.qty_ordered) * Number(l.unit_cost_cents));
  }
  const gst = gstOnTop(ex);
  return { ex_gst_cents: ex, gst_cents: gst, inc_gst_cents: ex + gst, blank_lines: blank };
}

/** `2026-10-30` as `30 Oct 2026`. A calendar day, so no time zone applies. */
export function dayLabel(day: string): string {
  const [y, m, d] = day.slice(0, 10).split('-').map(Number);
  const month = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][(m ?? 1) - 1];
  return `${d} ${month} ${y}`;
}
